import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';

interface Props { onBack: () => void; }

export default function PrivacyPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  return (
    <StaticPageLayout title={en ? 'Privacy Policy' : '隐私协议'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Privacy Policy - MinutesAI' : '隐私协议 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI Privacy Policy. Learn how we collect, use, and protect your data. Your privacy is our commitment.' : 'MinutesAI 隐私协议。了解我们如何收集、使用和保护您的数据。您的隐私是我们的承诺。'} />
        <link rel="canonical" href="https://minutesai.com/privacy" />
        <meta property="og:title" content={en ? 'Privacy Policy - MinutesAI' : '隐私协议 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/privacy" />
      </Helmet>

      <div className="bg-white rounded-2xl border border-surface-border p-8 md:p-12">
        <h2 className="text-3xl font-bold text-brand-900 font-serif mb-2">{en ? 'Privacy Policy' : '隐私协议'}</h2>
        <p className="text-sm text-ink-muted mb-10">{en ? 'Last updated: March 2026' : '最后更新：2026 年 3 月'}</p>

        <div className="space-y-10 text-ink-secondary leading-relaxed">

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '1. Introduction' : '1. 简介'}</h3>
            <p>
              {en
                ? 'MinutesAI ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered meeting transcription and intelligence platform. Please read this policy carefully. If you disagree with its terms, please discontinue use of our services.'
                : 'MinutesAI（"我们"）致力于保护您的隐私。本隐私协议说明了当您使用我们的 AI 会议转录和智能平台时，我们如何收集、使用、披露和保护您的信息。请仔细阅读本协议。如果您不同意其条款，请停止使用我们的服务。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '2. Information We Collect' : '2. 我们收集的信息'}</h3>
            <p className="mb-4">{en ? 'We collect the following categories of information:' : '我们收集以下类别的信息：'}</p>
            <div className="space-y-3 pl-4">
              <div>
                <span className="font-bold text-brand-900">{en ? 'Account information: ' : '账户信息：'}</span>
                {en ? 'Name, email address, phone number, and password when you register.' : '注册时提供的姓名、电子邮件地址、手机号码和密码。'}
              </div>
              <div>
                <span className="font-bold text-brand-900">{en ? 'Meeting content: ' : '会议内容：'}</span>
                {en ? 'Audio files you upload for transcription, and the resulting transcripts, summaries, and meeting notes generated by our AI.' : '您上传用于转录的音频文件，以及由此生成的转录文本、摘要和会议纪要。'}
              </div>
              <div>
                <span className="font-bold text-brand-900">{en ? 'Usage data: ' : '使用数据：'}</span>
                {en ? 'Information about how you interact with our platform, including features used, session duration, and device/browser information.' : '您与平台交互的信息，包括使用的功能、会话时长以及设备和浏览器信息。'}
              </div>
              <div>
                <span className="font-bold text-brand-900">{en ? 'Payment information: ' : '支付信息：'}</span>
                {en ? 'Billing details processed securely through our payment providers. We do not store full card numbers.' : '通过支付服务商安全处理的账单信息。我们不存储完整的银行卡号。'}
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '3. How We Use Your Information' : '3. 我们如何使用您的信息'}</h3>
            <p className="mb-4">{en ? 'We use the information we collect to:' : '我们使用收集的信息用于：'}</p>
            <ul className="space-y-2 pl-4 list-disc list-inside">
              <li>{en ? 'Provide, operate, and improve our transcription and meeting intelligence services' : '提供、运营和改进我们的转录和会议智能服务'}</li>
              <li>{en ? 'Process your audio files and generate meeting notes, summaries, and action items' : '处理您的音频文件并生成会议纪要、摘要和行动项'}</li>
              <li>{en ? 'Manage your account and communicate with you about your subscription' : '管理您的账户并就您的订阅与您沟通'}</li>
              <li>{en ? 'Process payments and prevent fraud' : '处理付款并防止欺诈'}</li>
              <li>{en ? 'Comply with legal obligations' : '遵守法律义务'}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '4. AI Model Training' : '4. AI 模型训练'}</h3>
            <div className="bg-brand-50 border border-brand-200/50 rounded-xl p-5 mb-4">
              <p className="font-bold text-brand-900 mb-2">{en ? 'Our commitment:' : '我们的承诺：'}</p>
              <p>{en ? 'We do not allow our AI model providers to use your meeting content or personal data to train their models.' : '我们不允许 AI 模型提供商使用您的会议内容或个人数据来训练其模型。'}</p>
            </div>
            <p className="mb-3">
              {en
                ? 'MinutesAI may use de-identified and anonymized data to improve our own transcription accuracy and AI capabilities. This data cannot be traced back to any individual user.'
                : 'MinutesAI 可能使用去标识化和匿名化的数据来提升我们自己的转录准确性和 AI 能力。这些数据无法追溯到任何个人用户。'}
            </p>
            <p>
              {en
                ? 'You can opt out of contributing anonymized data to model improvement at any time in your account settings. Enterprise customers have this disabled by default.'
                : '您可以随时在账户设置中选择退出向模型改进贡献匿名数据。企业客户默认禁用此功能。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '5. Data Storage & Security' : '5. 数据存储与安全'}</h3>
            <p className="mb-3">
              {en
                ? 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256. Our infrastructure is hosted on enterprise-grade cloud servers with VPC isolation, firewalls, and daily backups.'
                : '所有数据在传输过程中使用 TLS 1.3 加密，静态存储使用 AES-256 加密。我们的基础设施托管在具有 VPC 隔离、防火墙和每日备份的企业级云服务器上。'}
            </p>
            <p>
              {en
                ? 'Audio files and meeting notes are retained as long as your account is active. You can delete individual items or your entire account at any time.'
                : '音频文件和会议纪要在您的账户有效期间保留。您可以随时删除单条条目或整个账户。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '6. Data Sharing' : '6. 数据共享'}</h3>
            <p className="mb-3 font-bold text-brand-900">{en ? 'We do not sell your personal data.' : '我们不出售您的个人数据。'}</p>
            <p className="mb-3">
              {en
                ? 'We may share data with trusted service providers who help us operate our platform (such as cloud infrastructure, payment processors, and AI model providers), under strict data processing agreements that prohibit them from using your data for their own purposes.'
                : '我们可能在严格的数据处理协议下与帮助我们运营平台的可信服务提供商（如云基础设施、支付处理商和 AI 模型提供商）共享数据，这些协议禁止他们将您的数据用于自身目的。'}
            </p>
            <p>
              {en
                ? 'We may disclose information when required by law, to protect our rights, or in connection with a business transfer (in which case we will notify you in advance).'
                : '在法律要求、保护我们的权利或与业务转让相关时（届时我们将提前通知您），我们可能会披露信息。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '7. Data Retention' : '7. 数据保留'}</h3>
            <ul className="space-y-2 pl-4 list-disc list-inside">
              <li>{en ? 'Audio files: retained while your account is active' : '音频文件：在您的账户有效期间保留'}</li>
              <li>{en ? 'Meeting notes and content: retained while your account is active' : '会议纪要和内容：在您的账户有效期间保留'}</li>
              <li>{en ? 'Account data: deleted within 30 days of account closure' : '账户数据：账户关闭后 30 天内删除'}</li>
              <li>{en ? 'Payment records: retained as required by applicable financial regulations' : '支付记录：按适用财务法规要求保留'}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '8. Your Rights' : '8. 您的权利'}</h3>
            <p className="mb-4">{en ? 'You have the right to:' : '您有权：'}</p>
            <ul className="space-y-2 pl-4 list-disc list-inside">
              <li>{en ? 'Access and download a copy of your personal data' : '访问并下载您个人数据的副本'}</li>
              <li>{en ? 'Correct inaccurate or incomplete information' : '更正不准确或不完整的信息'}</li>
              <li>{en ? 'Delete your account and all associated data' : '删除您的账户及所有相关数据'}</li>
              <li>{en ? 'Opt out of anonymized data use for AI model training' : '选择退出将匿名数据用于 AI 模型训练'}</li>
              <li>{en ? 'Withdraw consent for optional data processing at any time' : '随时撤回对可选数据处理的同意'}</li>
              <li>{en ? 'Data portability — export your notes in standard formats' : '数据可携带性——以标准格式导出您的纪要'}</li>
            </ul>
            <p className="mt-4">
              {en
                ? 'To exercise any of these rights, contact us at official@minutes-ai.com. We will respond within 30 days.'
                : '如需行使上述任何权利，请联系 official@minutes-ai.com。我们将在 30 天内回复。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '9. Children\'s Privacy' : '9. 儿童隐私'}</h3>
            <p>
              {en
                ? 'Our services are not directed to individuals under the age of 16. We do not knowingly collect personal data from children. If you believe we have inadvertently collected such information, please contact us immediately.'
                : '我们的服务不面向 16 岁以下的个人。我们不会故意收集儿童的个人数据。如果您认为我们无意中收集了此类信息，请立即联系我们。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '10. Changes to This Policy' : '10. 本协议的变更'}</h3>
            <p>
              {en
                ? 'We may update this Privacy Policy from time to time. We will notify you of material changes via email or a prominent notice within the platform at least 14 days before the changes take effect. Continued use of our services after the effective date constitutes acceptance of the updated policy.'
                : '我们可能会不时更新本隐私协议。我们将在变更生效前至少 14 天通过电子邮件或平台内显著通知告知您重大变更。在生效日期后继续使用我们的服务即表示接受更新后的协议。'}
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-brand-900 mb-3">{en ? '11. Contact Us' : '11. 联系我们'}</h3>
            <p>
              {en
                ? 'For privacy-related questions, requests, or concerns, please contact our privacy team:'
                : '如有隐私相关问题、请求或疑虑，请联系我们的隐私团队：'}
            </p>
            <div className="mt-3 pl-4 space-y-1 text-brand-700">
              <p>official@minutes-ai.com</p>
              <p>{en ? 'Shanghai Minsi Zhihui AI Technology Co., Ltd.' : '上海敏思智汇人工智能科技有限公司'}</p>
            </div>
          </section>

        </div>
      </div>
    </StaticPageLayout>
  );
}
